import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { useStatus } from '../contexts/StatusContext';
import { checkStatus } from '../utils/checker';
import { normalizeInput } from '../utils/urlUtils';
import AdvancedSettings from './AdvancedSettings';
import * as FiIcons from 'react-icons/fi';

const { FiSearch, FiSettings, FiChevronDown, FiChevronUp } = FiIcons;

const InputCard = () => {
  const [input, setInput] = useState('');
  const [error, setError] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const { setCurrentResult, isChecking, setIsChecking, settings } = useStatus();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!input.trim()) {
      setError('Please enter a URL or domain');
      return;
    }

    setError('');
    setIsChecking(true);

    try {
      const normalizedUrl = normalizeInput(input.trim(), settings.protocol);
      const result = await checkStatus(normalizedUrl, {
        ...settings,
        thresholdMs: settings.thresholdMs
      });

      setCurrentResult({
        ...result,
        input: input.trim(),
        normalizedUrl,
        timestamp: new Date().toISOString(),
        id: Date.now().toString()
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setIsChecking(false);
    }
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    if (error) setError('');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-gray-800 rounded-xl card-shadow p-4 sm:p-6"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="url-input" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Website URL or Domain
          </label>
          <div className="relative">
            <input
              id="url-input"
              type="text"
              value={input}
              onChange={handleInputChange}
              placeholder="example.com or https://example.com"
              className="w-full px-4 py-3 pl-12 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus-ring transition-smooth text-sm sm:text-base"
              disabled={isChecking}
            />
            <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          </div>
          {error && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-2 text-sm text-red-600 dark:text-red-400"
            >
              {error}
            </motion.p>
          )}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center justify-center sm:justify-start space-x-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors focus-ring rounded px-2 py-1 order-2 sm:order-1"
          >
            <SafeIcon icon={FiSettings} className="h-4 w-4" />
            <span>Advanced Settings</span>
            <SafeIcon icon={showAdvanced ? FiChevronUp : FiChevronDown} className="h-4 w-4" />
          </button>

          <motion.button
            type="submit"
            disabled={isChecking || !input.trim()}
            className="w-full sm:w-auto px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors focus-ring text-sm sm:text-base order-1 sm:order-2"
            whileHover={{ scale: isChecking ? 1 : 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {isChecking ? (
              <span className="flex items-center justify-center space-x-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>
                  Checking<span className="loading-dots"></span>
                </span>
              </span>
            ) : (
              'Check Status'
            )}
          </motion.button>
        </div>

        {showAdvanced && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <AdvancedSettings />
          </motion.div>
        )}
      </form>
    </motion.div>
  );
};

export default InputCard;